import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShoppingBag, CalendarDays, Bell, Clock, FileText, ArrowRight, CreditCard, Truck, Receipt, Ban, BarChart3 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { api } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const statusColors = {
  pending: 'bg-[#D4AF37]/10 text-[#D4AF37]',
  preparing: 'bg-[#1E2D3D]/10 text-[#1E2D3D]',
  served: 'bg-[#5B6B3F]/10 text-[#5B6B3F]',
  accepted: 'bg-[#5B6B3F]/10 text-[#5B6B3F]',
  rejected: 'bg-[#8C3A3A]/10 text-[#8C3A3A]',
};

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)} EUR`;
}

function SectionCard({ title, count, badge, icon: Icon, children, href }) {
  return (
    <div className="bg-white rounded-xl border border-[#EBE8E0] p-5 shadow-[0_8px_30px_rgba(42,42,40,0.04)]">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#5B6B3F]" />
            <h2 className="font-heading text-lg font-semibold text-[#2A2A28]">{title}</h2>
          </div>
          <p className="text-xs text-[#7A7A75] mt-1">{badge}</p>
        </div>
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-[#8C3A3A] text-white text-sm font-semibold shadow-sm">
            {count}
          </span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
      {href && (
        <div className="mt-4">
          <Link to={href}>
            <Button variant="outline" className="rounded-xl border-[#EBE8E0] text-[#2A2A28]">
              Voir tout <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function RequestRow({ title, subtitle, meta, status, href }) {
  const statusLabelMap = {
    pending: 'En attente',
    preparing: 'En preparation',
    served: 'Servie',
    rejected: 'Refusee',
  };
  return (
    <Link to={href} className="block group">
      <div className="flex items-center justify-between gap-4 py-3 border-b border-[#EBE8E0] last:border-0">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#2A2A28] truncate group-hover:text-[#5B6B3F]">{title}</p>
          <p className="text-xs text-[#7A7A75] truncate">{subtitle}</p>
          {meta && <p className="text-[11px] text-[#7A7A75] mt-1">{meta}</p>}
        </div>
        <Badge className={`text-xs ${statusColors[status] || 'bg-gray-50 text-gray-600'}`}>{statusLabelMap[status] || status}</Badge>
      </div>
    </Link>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, tone = 'default' }) {
  const tones = {
    default: 'bg-white border-[#EBE8E0] text-[#2A2A28]',
    green: 'bg-[#5B6B3F]/6 border-[#5B6B3F]/20 text-[#2A2A28]',
    gold: 'bg-[#D4AF37]/8 border-[#D4AF37]/20 text-[#2A2A28]',
    red: 'bg-[#8C3A3A]/8 border-[#8C3A3A]/20 text-[#2A2A28]',
    blue: 'bg-[#1E2D3D]/8 border-[#1E2D3D]/20 text-[#2A2A28]',
  };

  return (
    <div className={`rounded-xl border p-5 shadow-[0_8px_24px_rgba(42,42,40,0.04)] ${tones[tone] || tones.default}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-[#7A7A75]">{title}</p>
          <p className="font-heading text-2xl font-bold mt-2">{value}</p>
        </div>
        <div className="rounded-xl bg-white/80 p-2 border border-white/60">
          <Icon className="w-5 h-5 text-[#5B6B3F]" />
        </div>
      </div>
      <p className="text-xs text-[#7A7A75]">{subtitle}</p>
    </div>
  );
}

function MiniBarChart({ title, items, valueKey = 'value', color = '#5B6B3F', formatter = (value) => value }) {
  const maxValue = Math.max(...items.map(item => Number(item[valueKey] || 0)), 1);

  return (
    <div className="bg-white rounded-xl border border-[#EBE8E0] p-5">
      <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">{title}</h3>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#7A7A75]">Aucune donnee disponible</p>}
        {items.map((item) => (
          <div key={item.label} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-[#2A2A28]">{item.label}</span>
              <span className="font-medium text-[#2A2A28]">{formatter(item[valueKey])}</span>
            </div>
            <div className="h-2 rounded-full bg-[#F1EBDD] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(Number(item[valueKey] || 0) / maxValue) * 100}%`, backgroundColor: color }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('day');

  const loadStats = useCallback(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const loadAnalytics = useCallback((nextPeriod = period) => {
    api.get(`/dashboard/analytics?period=${nextPeriod}`).then(r => setAnalytics(r.data)).catch(() => {});
  }, [period]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadAnalytics(period);
  }, [loadAnalytics, period]);

  useEffect(() => {
    const handleNotification = (event) => {
      if (['order', 'reservation', 'quote'].includes(event.detail?.type)) {
        loadStats();
        loadAnalytics(period);
      }
    };
    window.addEventListener('admin-notification', handleNotification);
    return () => window.removeEventListener('admin-notification', handleNotification);
  }, [loadAnalytics, loadStats, period]);

  const analyticsData = useMemo(() => {
    if (!analytics) {
      return {
        paymentRows: [],
        typeRows: [],
        hourlyRows: [],
        topDishRows: [],
        trendRows: [],
      };
    }

    return {
      paymentRows: Object.entries(analytics.payment_breakdown || {}).map(([label, value]) => ({
        label,
        count: value.count || 0,
        amount: value.amount || 0,
      })),
      typeRows: Object.entries(analytics.type_breakdown || {}).map(([label, value]) => ({
        label: label === 'delivery' ? 'Livraison' : label === 'takeaway' ? 'Emporter' : label === 'dine_in' ? 'Sur place' : label,
        count: value.count || 0,
        amount: value.amount || 0,
      })),
      hourlyRows: Object.entries(analytics.hourly_breakdown || {})
        .map(([label, value]) => ({ label, value }))
        .filter((item) => item.value > 0),
      topDishRows: (analytics.top_dishes || []).map((item) => ({
        label: item.name,
        value: item.quantity,
      })),
      trendRows: (analytics.trend || []).map((item) => ({
        label: item.label,
        value: item.revenue || 0,
        orders: item.orders || 0,
      })),
    };
  }, [analytics]);

  if (!stats) return <div className="text-[#7A7A75]">Chargement...</div>;

  const pendingOrders = stats.pending_orders || 0;
  const pendingReservations = stats.pending_reservations || 0;
  const pendingQuotes = stats.pending_quotes || 0;
  const totalPending = pendingOrders + pendingReservations + pendingQuotes;

  const cards = [
    { icon: ShoppingBag, label: 'Commandes du jour', value: stats.today_orders, sub: `${pendingOrders} a suivre`, color: 'text-[#5B6B3F]' },
    { icon: CalendarDays, label: 'Reservations du jour', value: stats.today_reservations, sub: `${pendingReservations} en attente`, color: 'text-[#D4AF37]' },
    { icon: FileText, label: 'Devis en attente', value: pendingQuotes, sub: `${stats.total_quotes || 0} demandes au total`, color: 'text-[#8C3A3A]' },
    { icon: Bell, label: 'Demandes à traiter', value: totalPending, sub: 'Commandes actives, réservations et devis en attente', color: 'text-[#7A7A75]' },
  ];
  const pendingOrdersList = (stats.recent_orders || []).filter(o => ['pending', 'preparing'].includes(o.status));
  const pendingReservationsList = (stats.recent_reservations || []).filter(r => r.status === 'pending');
  const pendingQuotesList = (stats.recent_quotes || []).filter(q => q.status === 'pending');
  const summary = analytics?.summary || {};

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2A2A28]">Tableau de bord</h1>
          <p className="text-sm text-[#7A7A75] mt-1">
            {totalPending > 0 ? `${totalPending} demande(s) demandent encore un suivi.` : 'Aucune demande en attente pour le moment.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 bg-[#8C3A3A]/10 text-[#8C3A3A] rounded-full px-3 py-1.5 text-sm font-medium">
            <Clock className="w-4 h-4" />
            Priorité
          </div>
          <div className="rounded-full border border-[#EBE8E0] bg-white p-1 flex items-center gap-1">
            {[
              { key: 'day', label: 'Jour' },
              { key: 'week', label: 'Semaine' },
              { key: 'month', label: 'Mois' },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setPeriod(option.key)}
                className={`rounded-full px-4 py-2 text-sm transition ${period === option.key ? 'bg-[#5B6B3F] text-white' : 'text-[#7A7A75] hover:text-[#2A2A28]'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#EBE8E0] p-5 shadow-[0_8px_30px_rgba(42,42,40,0.04)]" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <c.icon className={`w-5 h-5 ${c.color}`} />
              <span className="font-heading text-2xl font-bold text-[#2A2A28]">{c.value}</span>
            </div>
            <p className="text-sm font-medium text-[#2A2A28]">{c.label}</p>
            <p className="text-xs text-[#7A7A75] mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-[#5B6B3F]" />
          <h2 className="font-heading text-xl font-semibold text-[#2A2A28]">Analyse des commandes</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          <KpiCard title="Nombre de commandes" value={summary.orders_count || 0} subtitle={`Periode: ${period === 'day' ? '14 derniers jours' : period === 'week' ? '8 dernieres semaines' : '12 derniers mois'}`} icon={ShoppingBag} tone="green" />
          <KpiCard title="Montant total" value={formatCurrency(summary.revenue_total)} subtitle="Chiffre d'affaires cumule hors commandes refusees" icon={Receipt} tone="gold" />
          <KpiCard title="Panier moyen" value={formatCurrency(summary.avg_basket)} subtitle="Moyenne par commande validee" icon={CreditCard} tone="default" />
          <KpiCard title="TVA totale" value={formatCurrency(summary.total_vat)} subtitle="TVA calculee sur les commandes incluses" icon={Receipt} tone="blue" />
          <KpiCard title="Frais de livraison" value={formatCurrency(summary.delivery_total)} subtitle="Total cumule des frais de livraison" icon={Truck} tone="default" />
          <KpiCard title="Commandes refusees" value={summary.rejected_orders_count || 0} subtitle="Refus suivis a part pour analyse" icon={Ban} tone="red" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          <MiniBarChart
            title="Evolution du chiffre d'affaires"
            items={analyticsData.trendRows}
            valueKey="value"
            color="#5B6B3F"
            formatter={(value) => formatCurrency(value)}
          />
          <MiniBarChart
            title="Commandes par heure"
            items={analyticsData.hourlyRows}
            valueKey="value"
            color="#D4AF37"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <MiniBarChart
            title="Paiements"
            items={analyticsData.paymentRows.map((item) => ({ label: item.label, value: item.amount }))}
            valueKey="value"
            color="#1E2D3D"
            formatter={(value) => formatCurrency(value)}
          />
          <MiniBarChart
            title="Repartition des modes"
            items={analyticsData.typeRows.map((item) => ({ label: item.label, value: item.count }))}
            valueKey="value"
            color="#5B6B3F"
          />
          <MiniBarChart
            title="Articles les plus commandes"
            items={analyticsData.topDishRows}
            valueKey="value"
            color="#8C3A3A"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <SectionCard
          title="Commandes reçues"
          count={stats.pending_orders || 0}
          badge="Commandes en attente ou en preparation"
          icon={ShoppingBag}
          href="/admin/orders"
        >
          {pendingOrdersList.length === 0 && <p className="text-sm text-[#7A7A75]">Aucune commande active</p>}
          {pendingOrdersList.map(order => (
            <RequestRow
              key={order.id}
              title={order.customer?.name || 'Client'}
              subtitle={`${order.type === 'delivery' ? 'Livraison' : order.type === 'takeaway' ? 'À emporter' : 'Sur place'} - ${order.items?.length || 0} article(s)`}
              meta={`${order.total?.toFixed(2) || '0.00'} EUR`}
              status={order.status}
              href="/admin/orders"
            />
          ))}
        </SectionCard>

        <SectionCard
          title="Réservations reçues"
          count={stats.pending_reservations || 0}
          badge="Réservations à confirmer ou déplacer"
          icon={CalendarDays}
          href="/admin/reservations"
        >
          {pendingReservationsList.length === 0 && <p className="text-sm text-[#7A7A75]">Aucune réservation en attente</p>}
          {pendingReservationsList.map(res => (
            <RequestRow
              key={res.id}
              title={res.name}
              subtitle={`${res.date} à ${res.time} - ${res.guests} pers.`}
              meta={res.comment || 'Sans commentaire'}
              status={res.status}
              href="/admin/reservations"
            />
          ))}
        </SectionCard>

        <SectionCard
          title="Devis reçus"
          count={stats.pending_quotes || 0}
          badge="Demandes de devis en attente de réponse"
          icon={FileText}
          href="/admin/quotes"
        >
          {pendingQuotesList.length === 0 && <p className="text-sm text-[#7A7A75]">Aucun devis en attente</p>}
          {pendingQuotesList.map(quote => (
            <RequestRow
              key={quote.id}
              title={`${quote.guests} personnes`}
              subtitle={`${quote.event_date} - ${quote.event_address}`}
              meta={quote.email}
              status={quote.status}
              href="/admin/quotes"
            />
          ))}
        </SectionCard>
      </div>
    </div>
  );
}
